/**
 * Basket branding cookie utilities.
 *
 * When the public basket changes, a lightweight cookie is written with just
 * enough metadata (venue, event IDs, membership-group IDs) for the checkout
 * GSSP to resolve the correct branding config server-side.
 *
 * Branding resolution rules:
 *  - 1 event, 0 memberships  → event branding
 *  - 0 events, 1 membership  → membership_group branding
 *  - any other combination   → venue branding
 *  - empty basket             → no branding (null)
 */

import Cookies from "js-cookie";
import type { TicketBasketType } from "@shared/modules/payments/types";
import type {
  BrandingModelType,
  FetchBrandingConfigParams,
} from "./ssr-branding-utils";

export const BASKET_BRANDING_COOKIE = "sp_basket_branding";

export interface BasketBrandingContext {
  venueId: number;
  eventIds: number[];
  membershipGroupIds: number[];
  expiryDate?: string;
}

/**
 * Extracts the branding-relevant metadata from a basket.
 * Returns null when the basket is empty or has no items.
 */
export function extractBasketBrandingContext(
  basket: TicketBasketType | undefined
): BasketBrandingContext | null {
  if (!basket?.payment_venue || !basket.item_groups?.length) {
    return null;
  }

  const eventIds = new Set<number>();
  const membershipGroupIds = new Set<number>();

  basket.item_groups.forEach((item) => {
    if (typeof item.event === "number" && item.event) {
      eventIds.add(item.event);
    }
    if (typeof item.membership_group === "number" && item.membership_group) {
      membershipGroupIds.add(item.membership_group);
    }
  });

  return {
    venueId: basket.payment_venue as number,
    eventIds: [...eventIds],
    membershipGroupIds: [...membershipGroupIds],
    expiryDate: basket.expiry_date,
  };
}

/**
 * Determines which branding endpoint to call based on basket contents.
 *
 * - 1 event, 0 memberships  → event branding
 * - 0 events, 1 membership  → membership_group branding
 * - mixed / multiple        → venue branding
 * - empty                   → null (no branding)
 */
export function resolveBasketBrandingTarget(
  context: BasketBrandingContext | null
): FetchBrandingConfigParams | null {
  if (!context) return null;

  const { eventIds, membershipGroupIds, venueId } = context;
  const totalDistinctItems = eventIds.length + membershipGroupIds.length;

  if (totalDistinctItems === 0) return null;

  if (eventIds.length === 1 && membershipGroupIds.length === 0) {
    return { modelType: "event" as BrandingModelType, instanceId: eventIds[0] };
  }

  if (membershipGroupIds.length === 1 && eventIds.length === 0) {
    return {
      modelType: "membership_group" as BrandingModelType,
      instanceId: membershipGroupIds[0],
    };
  }

  return { modelType: "venue" as BrandingModelType, instanceId: venueId };
}

/**
 * Writes (or clears) the basket branding cookie.
 * Uses a compact JSON format to keep the cookie small.
 * Expires together with the basket when an expiry date is provided.
 */
export function setBasketBrandingCookie(
  context: BasketBrandingContext | null
): void {
  if (!context) {
    Cookies.remove(BASKET_BRANDING_COOKIE, { path: "/" });
    return;
  }

  const payload = JSON.stringify({
    v: context.venueId,
    e: context.eventIds,
    m: context.membershipGroupIds,
  });

  // Path must be "/" — Next.js client-side navigation fetches GSSP via
  // /_next/data/..., so a path-restricted cookie (e.g. "/checkout/") won't
  // be included in the request headers and the server won't see it.
  const options: Cookies.CookieAttributes = {
    path: "/",
    sameSite: "lax",
  };

  if (context.expiryDate) {
    options.expires = new Date(context.expiryDate);
  }

  Cookies.set(BASKET_BRANDING_COOKIE, payload, options);
}

/**
 * Parses the basket branding cookie value (from SSR request headers).
 * Returns null on missing / malformed data.
 */
export function parseBasketBrandingCookie(
  cookieValue: string | undefined
): BasketBrandingContext | null {
  if (!cookieValue) return null;

  try {
    const parsed = JSON.parse(cookieValue) as {
      v?: number;
      e?: number[];
      m?: number[];
    };

    if (!parsed.v) return null;

    return {
      venueId: parsed.v,
      eventIds: Array.isArray(parsed.e) ? parsed.e : [],
      membershipGroupIds: Array.isArray(parsed.m) ? parsed.m : [],
    };
  } catch {
    return null;
  }
}
